import React, { useState, useCallback, useRef, useEffect } from 'react';
import type { Itinerary, ModePreset, BikeSafetyProfile } from '../types/api';
import { planTrip } from '../utils/api';

interface UseTransitPlannerReturn {
  itineraries: Itinerary[];
  loading: boolean;
  error: string | null;
  mode: ModePreset;
  setMode: (m: ModePreset) => void;
  origin: { lat: number; lon: number } | null;
  destination: { lat: number; lon: number } | null;
  setOrigin: (loc: { lat: number; lon: number } | null) => void;
  setDestination: (loc: { lat: number; lon: number } | null) => void;
  date: string;
  setDate: (d: string) => void;
  time: string;
  setTime: (t: string) => void;
  arriveBy: boolean;
  setArriveBy: (a: boolean) => void;
  selectedItinerary: Itinerary | null;
  selectItinerary: (it: Itinerary | null) => void;
  search: () => void;
  otpDurationMs: number;
  bikePlannerDurationMs: number;
  maxTransfers: number;
  setMaxTransfers: (n: number) => void;
  bikeTransferWeight: number;
  setBikeTransferWeight: (n: number) => void;
  bikeSafety: BikeSafetyProfile;
  setBikeSafety: (p: BikeSafetyProfile) => void;
}

export function useTransitPlanner(): UseTransitPlannerReturn {
  const [itineraries, setItineraries] = useState<Itinerary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<ModePreset>('BICYCLE,TRANSIT');
  const [origin, setOrigin] = useState<{ lat: number; lon: number } | null>(null);
  const [destination, setDestination] = useState<{ lat: number; lon: number } | null>(null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  const [arriveBy, setArriveBy] = useState(false);
  const [selectedItinerary, selectItinerary] = useState<Itinerary | null>(null);
  const [otpDurationMs, setOtpDurationMs] = useState(0);
  const [bikePlannerDurationMs, setBikePlannerDurationMs] = useState(0);
  const [maxTransfers, setMaxTransfers] = useState(0);
  const [bikeTransferWeight, setBikeTransferWeight] = useState(5);
  const [bikeSafety, setBikeSafety] = useState<BikeSafetyProfile>('calm');

  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const search = useCallback(async () => {
    if (!origin || !destination) {
      setError('Set origin and destination');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await planTrip({
        origin,
        destination,
        date,
        time,
        arriveBy,
        mode,
        maxResults: 10,
        maxTransfers,
        bikeTransferWeight,
        bikeSafety,
      });
      setItineraries(res.itineraries || []);
      setOtpDurationMs(res.otpDurationMs || 0);
      setBikePlannerDurationMs(res.bikePlannerDurationMs || 0);
      if (res.itineraries?.length > 0) {
        selectItinerary(res.itineraries[0]);
      }
    } catch (err: any) {
      setError(err.message);
      setItineraries([]);
    } finally {
      setLoading(false);
    }
  }, [origin, destination, date, time, arriveBy, mode, maxTransfers, bikeTransferWeight, bikeSafety]);

  useEffect(() => {
    if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    if (itineraries.length > 0 && !document.hidden) {
      autoRefreshRef.current = setInterval(() => {
        if (!document.hidden) search();
      }, 60000);
    }
    return () => {
      if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    };
  }, [itineraries.length, search]);

  const handleVisibility = useCallback(() => {
    if (document.visibilityState === 'visible' && itineraries.length > 0) {
      search();
    }
  }, [search, itineraries.length]);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [handleVisibility]);

  return {
    itineraries,
    loading,
    error,
    mode,
    setMode,
    origin,
    destination,
    setOrigin,
    setDestination,
    date,
    setDate,
    time,
    setTime,
    arriveBy,
    setArriveBy,
    selectedItinerary,
    selectItinerary,
    search,
    otpDurationMs,
    bikePlannerDurationMs,
    maxTransfers,
    setMaxTransfers,
    bikeTransferWeight,
    setBikeTransferWeight,
    bikeSafety,
    setBikeSafety,
  };
}
